import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Loader2, Briefcase, Calendar, CalendarDays, TrendingUp,
  RefreshCw, Sparkles,
} from 'lucide-react';

import { Card, Button } from '../../ui-v2';
import { getDashboard } from '../api';

import BloqueAlertas from '../dashboard/BloqueAlertas';
import BloqueCampanasActivas from '../dashboard/BloqueCampanasActivas';
import BloqueProximasCitas from '../dashboard/BloqueProximasCitas';

/**
 * Dashboard v2 — vista panorámica admin.
 *
 * Cambios respecto a v1:
 *   - Quitado max-w-screen-2xl → ocupa todo el ancho disponible.
 *   - Mini-header sticky con título + contadores clave + botón refrescar.
 *     Se queda visible al scrollear largas listas de campañas / citas.
 */

export default function Dashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const cargar = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError(null);
    try {
      const res = await getDashboard();
      setData(res);
    } catch (e) {
      console.error('[Dashboard]', e);
      setError(e?.response?.data?.error || 'No se pudo cargar el dashboard.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  if (loading) {
    return (
      <div className="px-6 py-16 text-center text-sm text-slate-500">
        <Loader2 className="w-5 h-5 inline-block animate-spin mr-2" />
        Cargando dashboard…
      </div>
    );
  }
  if (error) {
    return (
      <div className="px-6 py-12 max-w-xl mx-auto">
        <div className="border border-red-200 bg-red-50 rounded-lg p-4 text-red-700 text-sm">
          {error}
        </div>
      </div>
    );
  }
  if (!data) return null;

  const { kpis, alertas, campanas_activas, proximas_citas } = data;

  return (
    <div className="min-h-full">
      {/* MINI-HEADER STICKY */}
      <div className="sticky top-14 z-30 bg-white/95 backdrop-blur-sm border-b border-slate-200">
        <div className="px-6 py-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-4 min-w-0">
            <h1 className="text-sm font-medium text-slate-900">Dashboard</h1>
            <div className="hidden md:flex items-center gap-3 text-xs text-slate-500">
              <span>
                <span className="text-slate-900 font-medium tabular-nums">{kpis.campanas_activas}</span>{' '}
                {kpis.campanas_activas === 1 ? 'campaña activa' : 'campañas activas'}
              </span>
              {kpis.citas_hoy_pendientes > 0 && (
                <>
                  <span className="text-slate-300">·</span>
                  <span>
                    <span className="text-slate-900 font-medium tabular-nums">
                      {kpis.citas_hoy_pendientes}
                    </span>{' '}
                    {kpis.citas_hoy_pendientes === 1 ? 'cita hoy' : 'citas hoy'}
                  </span>
                </>
              )}
              {kpis.progreso_promedio > 0 && (
                <>
                  <span className="text-slate-300">·</span>
                  <span className="text-primary font-medium tabular-nums">
                    {kpis.progreso_promedio}% progreso
                  </span>
                </>
              )}
              {alertas.length > 0 && (
                <>
                  <span className="text-slate-300">·</span>
                  <span className="text-amber-700">
                    {alertas.length} {alertas.length === 1 ? 'alerta' : 'alertas'}
                  </span>
                </>
              )}
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => cargar(true)}
            disabled={refreshing}
          >
            {refreshing ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <RefreshCw className="w-3.5 h-3.5" />
            )}
            Actualizar
          </Button>
        </div>
      </div>

      {/* CONTENIDO full width */}
      <div className="px-6 py-6">
        {/* Subtítulo (queda fuera del sticky para no saturarlo) */}
        <p className="text-sm text-slate-500 mb-5 -mt-1">
          Resumen del módulo de intervenciones técnicas.
        </p>

        {/* KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          <KpiCard
            label="Campañas activas"
            value={kpis.campanas_activas}
            icon={Briefcase}
            hint={
              kpis.campanas_borrador + kpis.campanas_pausadas > 0
                ? `+${kpis.campanas_borrador} en borrador, ${kpis.campanas_pausadas} pausadas`
                : null
            }
            colorVar="primary"
          />
          <KpiCard
            label="Citas hoy"
            value={kpis.citas_hoy_pendientes}
            icon={Calendar}
            hint={
              kpis.citas_hoy_realizadas > 0
                ? `${kpis.citas_hoy_realizadas} ya realizadas`
                : kpis.citas_hoy_total === 0
                  ? 'sin citas hoy'
                  : null
            }
          />
          <KpiCard
            label="Citas esta semana"
            value={kpis.citas_semana_pendientes}
            icon={CalendarDays}
            hint={
              kpis.citas_semana_total > 0
                ? `de ${kpis.citas_semana_total} totales`
                : null
            }
          />
          <KpiCard
            label="Progreso promedio"
            value={`${kpis.progreso_promedio}%`}
            icon={TrendingUp}
            hint={
              kpis.usuarios_pendientes_reservar > 0
                ? `${kpis.usuarios_pendientes_reservar} sin reservar`
                : '¡Todo el mundo con cita!'
            }
            colorVar="primary"
          />
        </div>

        {/* Estado vacío */}
        {kpis.campanas_activas === 0 && (
          <Card className="p-12 text-center mb-6">
            <Sparkles className="w-10 h-10 mx-auto mb-3 text-slate-300" />
            <h2 className="text-base font-medium text-slate-700">
              Aún no hay campañas activas
            </h2>
            <p className="text-sm text-slate-500 mt-1.5 max-w-md mx-auto">
              Crea una campaña desde el catálogo para empezar a gestionar
              intervenciones técnicas.
            </p>
            <Button
              className="mt-4"
              onClick={() => navigate('/citas-tecnicas/campanas')}
            >
              Ir a campañas
            </Button>
          </Card>
        )}

        {/* Alertas */}
        <BloqueAlertas alertas={alertas} />

        {/* Campañas activas + Próximas citas */}
        {kpis.campanas_activas > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
            <BloqueCampanasActivas campanas={campanas_activas} />
            <BloqueProximasCitas citas={proximas_citas} />
          </div>
        )}

        <div className="text-[11px] text-slate-400 text-center mt-8">
          Datos generados {formatTimestamp(data.generado_en)}
        </div>
      </div>
    </div>
  );
}

function KpiCard({ label, value, icon: Icon, hint, colorVar }) {
  const valorColor = colorVar === 'primary' ? 'text-primary' : 'text-slate-900';
  return (
    <Card className="p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[11px] text-slate-500 uppercase tracking-wider">
          {label}
        </div>
        {Icon && (
          <div className="w-8 h-8 rounded-full bg-slate-50 grid place-items-center">
            <Icon className="w-4 h-4 text-slate-400" />
          </div>
        )}
      </div>
      <div className={`text-3xl font-medium tabular-nums ${valorColor} leading-none`}>
        {value}
      </div>
      {hint && (
        <div className="text-[11px] text-slate-500 mt-2">{hint}</div>
      )}
    </Card>
  );
}

function formatTimestamp(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('es-ES', {
      day: '2-digit', month: 'short',
      hour: '2-digit', minute: '2-digit',
    });
  } catch (_) {
    return iso;
  }
}
